"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { get } from "../../../lib/request"
import {
  mapBrandSearchToDropdownOption,
  mapPropertySearchToDropdownOption,
  unwrapHotelSearchList,
} from "../../../lib/utils/hotelSearchMappers"
import SearchableDropdown from "../searchable-dropdown/SearchableDropdown"

export const BRAND_PAGE_SIZE = 25

export async function fetchBrandRows({ query = "", page = 0, size = BRAND_PAGE_SIZE } = {}) {
  let url = `/authn/brands/search?page=${page}&size=${size}`
  if (query.trim()) url += `&q=${encodeURIComponent(query.trim())}`
  return unwrapHotelSearchList(await get(url, null, { skipAuth: false }))
}

async function fetchSubBrandRows({ brandId, query = "", page = 0, size = BRAND_PAGE_SIZE } = {}) {
  if (!brandId) return []
  let url = `/authn/sub-brands/${brandId}/search?page=${page}&size=${size}`
  if (query.trim()) url += `&q=${encodeURIComponent(query.trim())}`
  return unwrapHotelSearchList(await get(url, null, { skipAuth: false }))
}

function mergeById(current, incoming) {
  const seen = new Set(current.map((row) => String(row?.id)))
  return [
    ...current,
    ...incoming.filter((row) => {
      if (row?.id == null || seen.has(String(row.id))) return false
      seen.add(String(row.id))
      return true
    }),
  ]
}

function toOptions(items, mapper, selectedId, selectedText) {
  const fromApi = (items || []).map(mapper).filter((opt) => opt.label && opt.id != null)
  if (selectedId != null && selectedText && !fromApi.some((opt) => String(opt.id) === String(selectedId))) {
    return [{ label: selectedText, value: selectedText, id: selectedId }, ...fromApi]
  }
  return fromApi
}

export default function BrandSubBrandFields({
  brandId = null,
  brandText = "",
  subBrandId = null,
  subBrandText = "",
  onBrandChange,
  onSubBrandChange,
  brandError = null,
  subBrandError = null,
  required = false,
  idPrefix = "brand-sub-brand",
  menuPortal = true,
}) {
  const [brands, setBrands] = useState([])
  const [brandQuery, setBrandQuery] = useState("")
  const [brandPage, setBrandPage] = useState(0)
  const [brandLast, setBrandLast] = useState(true)
  const [loadingBrands, setLoadingBrands] = useState(false)
  const [loadingMoreBrands, setLoadingMoreBrands] = useState(false)

  const [subBrands, setSubBrands] = useState([])
  const [subBrandQuery, setSubBrandQuery] = useState("")
  const [subBrandPage, setSubBrandPage] = useState(0)
  const [subBrandLast, setSubBrandLast] = useState(true)
  const [loadingSubBrands, setLoadingSubBrands] = useState(false)
  const [loadingMoreSubBrands, setLoadingMoreSubBrands] = useState(false)
  const loadedSubBrandForRef = useRef(null)

  const loadBrands = useCallback(async (query, { page = 0, append = false } = {}) => {
    if (append) setLoadingMoreBrands(true)
    else setLoadingBrands(true)
    try {
      const items = await fetchBrandRows({ query, page, size: BRAND_PAGE_SIZE })
      setBrandQuery(query)
      setBrandPage(page)
      setBrandLast(!items.length || items.length < BRAND_PAGE_SIZE)
      setBrands((prev) => (append ? mergeById(prev, items) : items))
    } catch (error) {
      console.error("Brand search failed:", error)
      if (!append) setBrands([])
      setBrandLast(true)
    } finally {
      setLoadingBrands(false)
      setLoadingMoreBrands(false)
    }
  }, [])

  const loadSubBrands = useCallback(async (nextBrandId, query, { page = 0, append = false } = {}) => {
    if (!nextBrandId) {
      setSubBrands([])
      setSubBrandLast(true)
      return
    }
    if (append) setLoadingMoreSubBrands(true)
    else setLoadingSubBrands(true)
    try {
      const items = await fetchSubBrandRows({ brandId: nextBrandId, query, page, size: BRAND_PAGE_SIZE })
      setSubBrandQuery(query)
      setSubBrandPage(page)
      setSubBrandLast(!items.length || items.length < BRAND_PAGE_SIZE)
      setSubBrands((prev) => (append ? mergeById(prev, items) : items))
    } catch (error) {
      console.error("Sub brand search failed:", error)
      if (!append) setSubBrands([])
      setSubBrandLast(true)
    } finally {
      setLoadingSubBrands(false)
      setLoadingMoreSubBrands(false)
    }
  }, [])

  useEffect(() => {
    loadBrands("", { page: 0, append: false })
  }, [loadBrands])

  useEffect(() => {
    if (!brandId) {
      loadedSubBrandForRef.current = null
      setSubBrands([])
      return
    }
    if (String(loadedSubBrandForRef.current) === String(brandId)) return
    loadedSubBrandForRef.current = brandId
    loadSubBrands(brandId, "", { page: 0, append: false })
  }, [brandId, loadSubBrands])

  const brandOptions = useMemo(
    () => toOptions(brands, mapBrandSearchToDropdownOption, brandId, brandText),
    [brands, brandId, brandText],
  )

  const subBrandOptions = useMemo(
    () => toOptions(subBrands, mapPropertySearchToDropdownOption, subBrandId, subBrandText),
    [subBrands, subBrandId, subBrandText],
  )

  const handleBrandChange = (text, option) => {
    const nextId = option?.id ?? null
    loadedSubBrandForRef.current = nextId
    setSubBrands([])
    onBrandChange?.(text, option)
    if (nextId) loadSubBrands(nextId, "", { page: 0, append: false })
  }

  return (
    <>
      <SearchableDropdown
        id={`${idPrefix}-brand`}
        name="brand"
        label="Brand"
        value={brandText}
        selectedId={brandId}
        onChange={handleBrandChange}
        onSearch={(query) => loadBrands((query || "").trim(), { page: 0, append: false })}
        options={brandOptions}
        placeholder="Select Brand"
        required={required}
        error={brandError}
        loading={loadingBrands}
        loadingMore={loadingMoreBrands}
        hasMore={!brandLast}
        onLoadMore={() => {
          if (brandLast || loadingBrands || loadingMoreBrands) return
          return loadBrands(brandQuery, { page: brandPage + 1, append: true })
        }}
        disableEnterSearch
        disableCustomKeyword
        menuPortal={menuPortal}
      />

      <SearchableDropdown
        id={`${idPrefix}-sub-brand`}
        name="subBrand"
        label="Sub Brand"
        value={subBrandText}
        selectedId={subBrandId}
        onChange={(text, option) => onSubBrandChange?.(text, option)}
        onSearch={(query) => {
          if (!brandId) return
          return loadSubBrands(brandId, (query || "").trim(), { page: 0, append: false })
        }}
        options={subBrandOptions}
        placeholder={brandId ? "Search sub brand" : "Select a brand first"}
        required={required}
        error={subBrandError}
        disabled={!brandId}
        loading={loadingSubBrands}
        loadingMore={loadingMoreSubBrands}
        hasMore={!subBrandLast}
        onLoadMore={() => {
          if (!brandId || subBrandLast || loadingSubBrands || loadingMoreSubBrands) return
          return loadSubBrands(brandId, subBrandQuery, { page: subBrandPage + 1, append: true })
        }}
        disableEnterSearch
        disableCustomKeyword
        menuPortal={menuPortal}
      />
    </>
  )
}
